import api from './api';

export interface PostCategorySummary {
  id: number;
  name: string;
  slug: string;
  icon?: string | null;
  color?: string | null;
}

export interface Post {
  id: number;
  title: string;
  content: string;
  video_url?: string;
  thumbnail_url?: string;
  status: 'pending' | 'approved' | 'rejected';
  tags: string[];
  pinned: boolean;
  likes_count: number;
  comments_count: number;
  is_liked?: boolean;
  created_at: string;
  updated_at?: string;
  approved_at?: string;
  rejected_reason?: string;
  category_id?: number | null;
  category?: PostCategorySummary | null;
  author: {
    id: number;
    username: string;
    full_name?: string;
    avatar?: string;
    role: string;
  };
  approved_by?: {
    id: number;
    username: string;
    full_name?: string;
  };
}

export interface PostListResponse {
  posts: Post[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export const postService = {
  getPosts: async (params?: {
    page?: number;
    per_page?: number;
    status?: string;
    category_id?: number;
    category_slug?: string;
    user_id?: number;
    search?: string;
  }) => {
    const response = await api.get<PostListResponse>('/posts', { params });
    return response.data;
  },

  getMyPosts: async (params?: { page?: number; per_page?: number; status?: string }) => {
    const response = await api.get<PostListResponse>('/posts/mine', { params });
    return response.data;
  },

  getPost: async (postId: number) => {
    const response = await api.get<{ post: Post }>(`/posts/${postId}`);
    return response.data.post;
  },

  createPost: async (payload: { title: string; content: string; tags?: string[]; video?: File; thumbnail?: File; category_id?: number | null }) => {
    const formData = new FormData();
    formData.append('title', payload.title);
    formData.append('content', payload.content);
    if (payload.tags && payload.tags.length > 0) {
      formData.append('tags', payload.tags.join(','));
    }
    if (payload.video) {
      formData.append('video', payload.video);
    }
    if (payload.thumbnail) {
      formData.append('thumbnail', payload.thumbnail);
    }
    if (payload.category_id !== undefined && payload.category_id !== null) {
      formData.append('category_id', payload.category_id.toString());
    }
    
    // Don't set Content-Type header manually - let axios set it automatically with boundary
    // Setting it manually causes 415 error
    const response = await api.post<{ message: string; post: Post; requires_approval?: boolean }>('/posts', formData);
    return response.data;
  },

  updatePost: async (postId: number, payload: { title?: string; content?: string; tags?: string[]; follower_ids?: number[]; category_id?: number | null }) => {
    const response = await api.put<{ message: string; post: Post }>(`/posts/${postId}`, payload);
    return response.data;
  },

  deletePost: async (postId: number) => {
    const response = await api.delete<{ message: string }>(`/posts/${postId}`);
    return response.data;
  },

  approvePost: async (postId: number) => {
    const response = await api.post<{ message: string; post: Post }>(`/posts/${postId}/approve`);
    return response.data;
  },

  rejectPost: async (postId: number, reason?: string) => {
    const response = await api.post<{ message: string; post: Post }>(`/posts/${postId}/reject`, { reason });
    return response.data;
  },

  togglePinPost: async (postId: number) => {
    const response = await api.post<{ message: string; post: Post }>(`/posts/${postId}/pin`);
    return response.data;
  },

  getPendingPosts: async (params?: { page?: number; per_page?: number }) => {
    const response = await api.get<PostListResponse>('/posts/pending', { params });
    return response.data;
  },

  toggleLikePost: async (postId: number) => {
    const response = await api.post<{ message: string; is_liked: boolean; likes_count: number }>(`/posts/${postId}/like`);
    return response.data;
  },

  getComments: async (postId: number) => {
    const response = await api.get<{ comments: Comment[] }>(`/posts/${postId}/comments`);
    return response.data.comments;
  },

  createComment: async (postId: number, content: string, parentId?: number, options?: { attachment?: File; linkUrl?: string; videoUrl?: string; videoFile?: File }) => {
    if (options?.attachment || options?.videoFile) {
      // Use FormData for file uploads
      const formData = new FormData();
      formData.append('content', content);
      if (parentId) {
        formData.append('parent_id', parentId.toString());
      }
      if (options.attachment) {
        formData.append('attachment', options.attachment);
      }
      if (options.videoFile) {
        formData.append('video', options.videoFile);
      }
      if (options.linkUrl) {
        formData.append('link_url', options.linkUrl);
      }
      if (options.videoUrl) {
        formData.append('video_url', options.videoUrl);
      }
      
      // Don't set Content-Type header manually - let axios set it automatically with boundary
      const response = await api.post<{ message: string; comment: Comment }>(`/posts/${postId}/comments`, formData);
      return response.data;
    } else {
      // Use JSON for text-only comments
      const response = await api.post<{ message: string; comment: Comment }>(`/posts/${postId}/comments`, { 
        content, 
        parent_id: parentId,
        link_url: options?.linkUrl,
        video_url: options?.videoUrl
      });
      return response.data;
    }
  },

  deleteComment: async (postId: number, commentId: number) => {
    const response = await api.delete<{ message: string }>(`/posts/${postId}/comments/${commentId}`);
    return response.data;
  },

  toggleCommentLike: async (postId: number, commentId: number) => {
    const response = await api.post<{ message: string; is_liked: boolean; likes_count: number }>(
      `/posts/${postId}/comments/${commentId}/like`
    );
    return response.data;
  },

  reportPost: async (postId: number, reason: string) => {
    const response = await api.post<{ message: string }>(`/posts/${postId}/report`, { reason });
    return response.data;
  },

  getReports: async (params?: { page?: number; per_page?: number; status?: string }) => {
    const response = await api.get<{ reports: any[]; total: number; page: number; per_page: number; pages: number }>('/posts/reports', { params });
    return response.data;
  },

  reviewReport: async (reportId: number, action: 'reviewed' | 'resolved') => {
    const response = await api.post<{ message: string; report: any }>(`/posts/reports/${reportId}/review`, { action });
    return response.data;
  },

  getUserPosts: async (userId: number, params?: { page?: number; per_page?: number }) => {
    const response = await api.get<PostListResponse>('/posts', { 
      params: { ...params, user_id: userId } 
    });
    return response.data;
  },
};

export interface Comment {
  id: number;
  post_id: number;
  parent_id?: number;
  content: string;
  attachment_url?: string;
  link_url?: string;
  video_url?: string;
  created_at: string;
  updated_at?: string;
  likes_count?: number;
  is_liked?: boolean;
  replies?: Comment[];
  author: {
    id: number;
    username: string;
    full_name?: string;
    avatar?: string;
    role: string;
  };
}

